import { useTranslation } from "react-i18next";
import { Button, Icon } from "semantic-ui-react";

import { Can, Dropdown, ResetFiltersButton } from "components";
import { SEASONS } from "models";
import { DebouncedInput } from "modules/TyresDashboard/components/TyresDashboardFilters/components";
import { DEFAULT_TYRE_FILTERS, setTyresFilters, useTyresFilters } from "modules/TyresDashboard/components/TyresDashboardFilters/hooks";
import "modules/TyresDashboard/components/TyresDashboardFilters/TyresDashboardFilters.scss";
import { enumToDropdownOptions } from "utils/common";

type TyresDashboardFiltersProps = {
  onOpenCreateModal: () => void;
};

const toTextFilter = (value: string) => (value.trim() === "" ? null : value);
const toIntFilter = (value: string) => (Number.isNaN(parseInt(value)) ? null : parseInt(value));
const toFloatFilter = (value: string) => (Number.isNaN(parseFloat(value)) ? null : parseFloat(value));

export const TyresDashboardFilters = ({ onOpenCreateModal }: TyresDashboardFiltersProps) => {
  const { t } = useTranslation();

  const tyreSeasonFilterOptions = enumToDropdownOptions({ enumObj: SEASONS, t });
  const { manufacturer, description, ean, season, carry_capacity, width, height, size, speed } = useTyresFilters();

  return (
    <div className="TyresDashboardFilters">
      <div className="row">
        <Can I="create" the="tyres">
          <Button basic icon onClick={onOpenCreateModal}>
            <Icon className="plus" />
          </Button>
        </Can>
        <DebouncedInput
          value={manufacturer}
          placeholder={t("v8_manufacturer", "Manufacturer")}
          onChangeDebounced={value => setTyresFilters({ manufacturer: toTextFilter(value), page: 1 })}
        />
        <DebouncedInput
          value={description}
          placeholder={t("v8_description", "Description")}
          onChangeDebounced={value => setTyresFilters({ description: toTextFilter(value), page: 1 })}
        />
        <DebouncedInput value={ean} placeholder="EAN" onChangeDebounced={value => setTyresFilters({ ean: toTextFilter(value), page: 1 })} />
        <Dropdown
          fluid
          selection
          clearable
          selectOnBlur={false}
          value={season ?? undefined}
          options={tyreSeasonFilterOptions}
          placeholder={t("v8_season", "Season")}
          onChange={season => setTyresFilters({ season: season ?? null, page: 1 })}
        />
        <ResetFiltersButton handleReset={() => setTyresFilters(DEFAULT_TYRE_FILTERS)} />
      </div>

      <div className="row">
        <DebouncedInput
          isNumeric
          value={carry_capacity}
          placeholder={t("v8_carry_capacity", "Carry capacity")}
          onChangeDebounced={value => setTyresFilters({ carry_capacity: toIntFilter(value), page: 1 })}
        />
        <DebouncedInput
          isNumeric
          value={width}
          placeholder={t("v8_width", "Width")}
          onChangeDebounced={value => setTyresFilters({ width: toIntFilter(value), page: 1 })}
        />
        <DebouncedInput
          isNumeric
          value={height}
          placeholder={t("v8_height", "Height")}
          onChangeDebounced={value => setTyresFilters({ height: toIntFilter(value), page: 1 })}
        />
        <DebouncedInput isNumeric value={size} placeholder={t("v8_size", "Size")} onChangeDebounced={value => setTyresFilters({ size: toFloatFilter(value), page: 1 })} />
        <DebouncedInput value={speed} placeholder={t("v8_speed", "Speed")} onChangeDebounced={value => setTyresFilters({ speed: toTextFilter(value), page: 1 })} />
      </div>
    </div>
  );
};
